import { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const pricingPlans = [
  {
    title: 'Starter',
    price: {
      monthly: 49,
      yearly: 39,
    },
    description: 'Perfect for small restaurants and cafes',
    features: [
      'Up to 2 POS terminals',
      'Basic inventory management',
      'Daily sales reports',
      'Email support',
      'Mobile app access',
      'Basic table management',
    ],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    title: 'Standard',
    price: {
      monthly: 99,
      yearly: 79,
    },
    description: 'Ideal for growing restaurants',
    features: [
      'Up to 5 POS terminals',
      'Advanced inventory management',
      'Real-time analytics',
      '24/7 phone & email support',
      'Mobile app access',
      'Advanced table management',
      'Loyalty program',
      'Online ordering integration',
    ],
    cta: 'Start Free Trial',
    popular: true,
  },
  {
    title: 'Enterprise',
    price: {
      monthly: 199,
      yearly: 159,
    },
    description: 'For multi-location restaurants',
    features: [
      'Unlimited POS terminals',
      'Multi-location management',
      'Custom reporting',
      'Dedicated account manager',
      'API access',
      'Advanced security features',
      'Custom integrations',
      'Priority support',
    ],
    cta: 'Contact Sales',
    popular: false,
  },
];

function Pricing() {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const handleBillingCycleChange = (event, newBillingCycle) => {
    if (newBillingCycle !== null) {
      setBillingCycle(newBillingCycle);
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            align="center"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 2,
            }}
          >
            Simple, Transparent Pricing
          </Typography>
          <Typography
            variant="h5"
            align="center"
            sx={{ opacity: 0.9 }}
          >
            Choose the plan that's right for your restaurant
          </Typography>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <ToggleButtonGroup
            value={billingCycle}
            exclusive
            onChange={handleBillingCycleChange}
            aria-label="billing cycle"
          >
            <ToggleButton value="monthly" aria-label="monthly billing">
              Monthly
            </ToggleButton>
            <ToggleButton value="yearly" aria-label="yearly billing">
              Yearly (Save 20%)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={4}>
          {pricingPlans.map((plan) => (
            <Grid item xs={12} md={4} key={plan.title}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  ...(plan.popular && {
                    border: '2px solid',
                    borderColor: 'primary.main',
                  }),
                }}
              >
                {plan.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderBottomLeftRadius: 8,
                    }}
                  >
                    Most Popular
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Typography variant="h4" gutterBottom>
                    {plan.title}
                  </Typography>
                  <Typography
                    variant="h3"
                    color="primary"
                    sx={{ mb: 2 }}
                  >
                    ${plan.price[billingCycle]}
                    <Typography
                      component="span"
                      variant="h6"
                      color="text.secondary"
                    >
                      /month
                    </Typography>
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                  >
                    {plan.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <List>
                    {plan.features.map((feature) => (
                      <ListItem key={feature} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <Box sx={{ p: 4, pt: 0 }}>
                  <Button
                    component={RouterLink}
                    to={plan.title === 'Enterprise' ? '/contact' : '/demo'}
                    variant={plan.popular ? 'contained' : 'outlined'}
                    color="primary"
                    size="large"
                    fullWidth
                  >
                    {plan.cta}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* FAQ Section */}
        <Box sx={{ mt: 10, textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom>
            Have Questions?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            We're here to help you choose the right plan
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            color="primary"
            size="large"
          >
            Contact Sales
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default Pricing; 